import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../core/consts/api.const';
import { LoadingService } from '../../services/loading.service';
import { RockModelsService } from '../../services/rock-models/rock-models.service';
import { SnackbarService } from '../../services/snackbar.service';
import { catchError, concatMap, EMPTY, map, Observable, of, tap, throwError } from 'rxjs';
import { UpscalingRockFile, PhaseInfo } from '../../core/models/upscaling-rock-files.model';
import { RockImagesService } from '../../services/rock-images/rock-images.service';
import { RockThumbnailService } from '../../services/rock-thumbnail/rock-thumbnail.service';
import {
  UpscalingRockFileModelShortDto,
} from '../../core/models/upscaling-rock-file-models.model';
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
} from '../../common/popups/confirm-popup/confirm-popup.component';
import { RemoveExtensionPipe } from '../../common/pipes/remove-extension.pipe';

interface RockModelManagerDataUi {
  fileName: string;
  size: string;
  phasesCount: number;
  voxelsX?: number;
  voxelsY?: number;
  voxelsZ?: number;
  rockModels: UpscalingRockFileModelShortDto[];
  thumbnailUrl?: string;
}

@Component({
  selector: 'app-rock-models',
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTableModule,
    RemoveExtensionPipe,
  ],
  templateUrl: './rock-models.component.html',
  styleUrl: './rock-models.component.scss',
  standalone: true,
})
export class RockModelsComponent extends Destroyable(Object) implements OnInit {
  private readonly componentId: string = 'rock-models';
  isLoading = false;
  fileName: string = '';
  rockModelData: RockModelManagerDataUi | null = null;
  selectedModel: UpscalingRockFileModelShortDto | null = null;
  thumbnailUrl: string | null = null;
  rockFile: UpscalingRockFile | null = null;
  phases: PhaseInfo[] = [];

  get totalFraction(): number {
    return this.phases.reduce((sum, phase) => sum + phase.fraction, 0);
  }
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly loadingService: LoadingService,
    private readonly rockModelsService: RockModelsService,
    private readonly rockImagesService: RockImagesService,
    private readonly rockThumbnailService: RockThumbnailService,
    private readonly snackbarService: SnackbarService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.route.queryParams.pipe(this.takeUntilDestroyed()).subscribe((params) => {
      this.fileName = params['fileName'] || '';
      if (this.fileName) {
        this.rockModelData = {
          fileName: this.fileName,
          size: '',
          phasesCount: 0,
          rockModels: [],
        };
        this.loadRockModels();
        this.loadThumbnail();
      } else {
        this.router.navigate(['/upscaling/rock-images']);
      }
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.thumbnailUrl) {
      this.rockThumbnailService.revokeThumbnailUrl(this.thumbnailUrl);
    }
  }

  onRowClick(model: UpscalingRockFileModelShortDto): void {
    if (this.selectedModel?.id === model.id) {
      this.selectedModel = null;
    } else {
      this.selectedModel = model;
    }
  }

  private processPhases(): void {
    this.phases = [];
    if (!this.rockFile) return;
    if (this.rockFile.phases) {
      this.phases = this.rockFile.phases;
      if (this.rockFile.normalizedPhases) {
        this.phases.forEach(phase => {
          const normalizedValue = this.rockFile!.normalizedPhases![phase.label.toString()];
          if (normalizedValue !== undefined) {
            phase.color = `rgb(${normalizedValue}, ${normalizedValue}, ${normalizedValue})`;
          } else {
            phase.color = 'rgb(128, 128, 128)';
          }
        });
      } else {
        const maxPhaseValue = this.rockFile.maxPhaseValue || 255;
        this.phases.forEach(phase => {
          const grayValue = Math.round((phase.label / maxPhaseValue) * 255);
          phase.color = `rgb(${grayValue}, ${grayValue}, ${grayValue})`;
        });
      }
    } else if (this.rockFile.fractionHistogram) {
      this.phases = Object.entries(this.rockFile.fractionHistogram).map(([label, fraction], index) => {
        const labelNum = parseInt(label, 10);
        let grayValue = 128;
        if (this.rockFile!.normalizedPhases && this.rockFile!.normalizedPhases[label] !== undefined) {
          grayValue = this.rockFile!.normalizedPhases[label];
        } else if (this.rockFile!.maxPhaseValue) {
          grayValue = Math.round((labelNum / this.rockFile!.maxPhaseValue) * 255);
        }
        return {
          phase: index,
          label: labelNum,
          fraction: fraction,
          color: `rgb(${grayValue}, ${grayValue}, ${grayValue})`,
        };
      });
    }
  }

  newRockModel(): void {
    if (!this.fileName) {
      this.snackbarService.openCustom({
        message: 'No rock file selected',
        title: 'Error',
        type: 'error',
        action: 'Close',
      });
      return;
    }

    this.router.navigate(['/phase-definition'], {
      queryParams: { fileName: this.fileName }
    });
  }

  previewRockModel(): void {
    if (!this.selectedModel || !this.fileName) return;

    this.router.navigate(['/phase-definition'], {
      queryParams: { 
        fileName: this.fileName,
        modelId: this.selectedModel.id
      }
    });
  }

  duplicateRockModel(): void {
    if (!this.selectedModel || !this.fileName) return;

    this.router.navigate(['/phase-definition'], {
      queryParams: {
        fileName: this.fileName,
        sourceModelId: this.selectedModel.id,
      },
    });
  }

  deleteRockModel(): void {
    if (!this.selectedModel) return;

    const dialogData: ConfirmPopupData = {
      title: 'Delete rock model',
      message: `Do you want to delete the rock model "${this.selectedModel.name}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    };

    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      minWidth: '500px',
      maxWidth: '700px',
      minHeight: '200px',
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(
        concatMap((result: boolean) => (result ? this.performDeleteSelectedModel() : EMPTY)),
        this.takeUntilDestroyed(),
      )
      .subscribe();
  }

  private performDeleteSelectedModel(): Observable<void> {
    if (!this.selectedModel || !this.fileName) return EMPTY;

    const modelId = this.selectedModel.id;
    const modelName = this.selectedModel.name;

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.loadingService.startLoading(this.componentId);

    return this.rockModelsService.deleteUpscalingRockFileModel(this.fileName, modelId, options).pipe(
      tap(() => {
        this.selectedModel = null;
        if (this.rockModelData) {
          this.rockModelData = {
            ...this.rockModelData,
            rockModels: this.rockModelData.rockModels.filter((m) => m.id !== modelId),
          };
        }
        this.snackbarService.openCustom({
          message: `Rock model "${modelName}" deleted successfully`,
          title: 'Success',
          type: 'success',
          action: 'Close',
        });
      }),
      concatMap(() => this.reloadModelsOnly(options)),
      tap(() => this.loadingService.stopLoading(this.componentId)),
    );
  }

  getSizeString(): string {
    if (!this.rockModelData) return '';
    const { voxelsX, voxelsY, voxelsZ } = this.rockModelData;
    if (voxelsX && voxelsY && voxelsZ) {
      return `${voxelsX}x${voxelsY}x${voxelsZ}`;
    }
    return this.rockModelData.size || '';
  }

  private loadRockModels(): void {
    this.loadingService.startLoading(this.componentId);

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.rockImagesService
      .getRockFileMetadata(this.fileName, 'Upscaling')
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (metadata) => {
          const upscalingFile = metadata as UpscalingRockFile;

          const phasesCount =
            upscalingFile.numberOfPhases ||
            (upscalingFile.fractionHistogram ? Object.keys(upscalingFile.fractionHistogram).length : 0);

          const x = upscalingFile.voxelsX;
          const y = upscalingFile.voxelsY;
          const z = upscalingFile.voxelsZ;
          const size = x && y && z ? `${x}x${y}x${z}` : (this.rockModelData?.size ?? '');

          this.rockModelData = {
            ...(this.rockModelData ?? {
              fileName: this.fileName,
              size: '',
              phasesCount: 0,
              rockModels: [],
            }),
            fileName: upscalingFile.fileName || this.fileName,
            size,
            phasesCount,
            voxelsX: x,
            voxelsY: y,
            voxelsZ: z,
          };

          this.rockFile = upscalingFile;
          this.selectedModel = null;
          this.processPhases();
          this.loadingService.stopLoading(this.componentId);
        },
        error: () => {
          this.loadingService.stopLoading(this.componentId);
          this.snackbarService.openCustom({
            message: 'Failed to load rock file metadata',
            title: 'Error',
            type: 'error',
            action: 'Close',
          });
        },
      });

    this.rockModelsService
      .getUpscalingRockFileModels(this.fileName, options)
      .pipe(
        catchError((err: unknown) => (this.isNotFoundError(err) ? of([]) : throwError(() => err))),
        this.takeUntilDestroyed(),
      )
      .subscribe({
        next: (models) => {
          this.rockModelData = {
            ...(this.rockModelData ?? {
              fileName: this.fileName,
              size: '',
              phasesCount: 0,
              rockModels: [],
            }),
            rockModels: models,
          };
        },
        error: () => {
          this.snackbarService.openCustom({
            message: 'Failed to load rock models list',
            title: 'Error',
            type: 'error',
            action: 'Close',
          });
        },
      });
  }

  private reloadModelsOnly(options: any): Observable<void> {
    if (!this.fileName || !this.rockModelData) return EMPTY;

    return this.rockModelsService.getUpscalingRockFileModels(this.fileName, options).pipe(
      catchError((err: unknown) => (this.isNotFoundError(err) ? of([]) : throwError(() => err))),
      tap((models) => {
        this.rockModelData = {
          ...this.rockModelData!,
          rockModels: models,
        };
      }),
      map(() => void 0),
    );
  }

  private isNotFoundError(err: unknown): boolean {
    return err instanceof HttpErrorResponse && err.status === 404;
  }

  private loadThumbnail(): void {
    this.rockThumbnailService.getThumbnail(this.fileName, 'Upscaling').subscribe({
      next: (url: string | null) => {
        this.thumbnailUrl = url;
      },
      error: () => {
        this.thumbnailUrl = null;
      },
    });
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((loadingState: boolean) => {
        this.isLoading = loadingState;
      });
  }
}
